import 'bootstrap/dist/css/bootstrap.rtl.min.css'
import 'bootstrap/dist/js/bootstrap.bundle.min.js'
import 'bootstrap-icons/font/bootstrap-icons.css'
import './theme/theme.css'

import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router'
import { Toaster } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { Capacitor } from '@capacitor/core'
import useAuth from '../context/auth.context.jsx'
import useGroup, { GroupProvider } from '../context/group.context.jsx'
import { TourProvider } from '../context/tour.context.jsx'
import LegalConsentGate from './components/legalConsentGate.jsx'

import Login from './pages/connections/login.jsx'
import Register from './pages/connections/register.jsx'
import Logout from './pages/connections/logout.jsx'
import JoinByCode from './pages/connections/joinByCode.jsx'
import ReferralLanding from './pages/connections/referralLanding.jsx'
import PendingInviteConsumer from './components/pendingInviteConsumer.jsx'
import PlanLimitBanner from './components/planLimitBanner.jsx'


// Split per route: a signed-out visitor should not download the dashboard,
// the charts library or the legal pages just to see the login form.
const Home = lazy(() => import('./pages/home.jsx'))
const ExpensesList = lazy(() => import('./pages/expensesList.jsx'))
const Report = lazy(() => import('./pages/report.jsx'))
const Receipts = lazy(() => import('./pages/receipts.jsx'))
const ShoppingListPage = lazy(() => import('./pages/shoppingList.jsx'))
const TasksPage = lazy(() => import('./pages/tasks.jsx'))
const Workspace = lazy(() => import('./pages/workspace.jsx'))
const GroupPage = lazy(() => import('./pages/group.jsx'))
const Settings = lazy(() => import('./pages/settings.jsx'))
const MySubscription = lazy(() => import('./pages/mySubscription.jsx'))
const Plans = lazy(() => import('./pages/plans.jsx'))
// ponytail: Friends route frozen — page file kept; see 2026-08-04-friends-design.md
const Games = lazy(() => import('./pages/games.jsx'))
const GamePage = lazy(() => import('./pages/gamePage.jsx'))
const GuestShell = lazy(() => import('./pages/guest/guestShell.jsx'))
const Welcome = lazy(() => import('./pages/onboarding/welcome.jsx'))
const ForgotPassword = lazy(() => import('./pages/connections/forgotPassword.jsx'))
const ResetPassword = lazy(() => import('./pages/connections/resetPassword.jsx'))
const VerifyEmail = lazy(() => import('./pages/connections/verifyEmail.jsx'))
const PublicLayout = lazy(() => import('./pages/public/PublicLayout.jsx'))
const About = lazy(() => import('./pages/public/About.jsx'))
const Install = lazy(() => import('./pages/public/Install.jsx'))
const Faq = lazy(() => import('./pages/public/Faq.jsx'))
const Pricing = lazy(() => import('./pages/public/Pricing.jsx'))
const LegalPage = lazy(() => import('./pages/public/LegalPage.jsx'))
const DeleteAccount = lazy(() => import('./pages/public/DeleteAccount.jsx'))
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout.jsx'))
const AdminGuard = lazy(() => import('./pages/admin/AdminGuard.jsx'))

import { DEFAULT_PUBLIC_LANG, PUBLIC_LANGS, publicPath } from './pages/public/publicPaths'
import AppBar from './components/appBar.jsx'
import LegacyDomainNotice from './components/legacyDomainNotice.jsx'
import Navbar from './components/navbar.jsx'
import Sidebar from './components/sidebar.jsx'
import useIsDesktop from './hooks/useIsDesktop.js'

// Chunks are small and same-origin, so this is usually a single frame. It exists
// so the screen never goes blank on a slow connection.
function RouteFallback() {
  const { t } = useTranslation('common');
  return (
    <div className="route-loading" role="status" aria-live="polite">
      <span className="spinner-border" aria-hidden="true" />
      <span className="visually-hidden">{t('loading')}</span>
    </div>
  );
}

// Group-scoped routes live inside GroupProvider so they can read the active
// group. Every one of these pages keeps the fetched group's data in its own
// state and refetches when the group changes, which leaves the previous
// group's expenses, balances and totals on screen under the new group's name
// until the request lands. Keying them on the group id remounts instead, so a
// switch starts from empty rather than from someone else's numbers. Settings
// and games are deliberately not keyed: a game in progress outweighs the one
// piece of group data games do hold — usePuzzleGame keeps the leaderboard of
// whichever group was active when it loaded, which is a smaller wrong than
// wiping someone's half-finished puzzle.
function GroupScopedRoutes({ isDesktop }) {
  const { activeGroup } = useGroup();
  const gid = activeGroup?._id || 'none';

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<Home key={gid} />} />
        <Route path="/expenses" element={<ExpensesList key={gid} />} />
        <Route path="/report" element={<Report key={gid} />} />
        <Route path="/receipts" element={<Receipts />} />
        <Route path="/shopping" element={isDesktop ? <Workspace key={gid} /> : <ShoppingListPage key={gid} />} />
        <Route path="/tasks" element={isDesktop ? <Workspace key={gid} /> : <TasksPage key={gid} />} />
        <Route path="/group" element={<GroupPage key={gid} />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/subscription" element={<MySubscription />} />
        <Route path="/plans" element={<Plans />} />
        <Route path="/games" element={<Games />} />
        {/* One route for every game; gamePage sends an unknown one back to
            the hub. */}
        <Route path="/games/:game" element={<GamePage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function ProtectedShell() {
  const { user } = useAuth();
  const isDesktop = useIsDesktop();
  const { t } = useTranslation('common');
  if (!user) return <Navigate to="/login" replace />;

  const skipLink = (
    <a className="app-skip-link" href="#app-content">{t('skipToContent')}</a>
  );
  const main = (
    <main id="app-content" className="app-main" tabIndex={-1}>
      <GroupScopedRoutes isDesktop={isDesktop} />
    </main>
  );

  return (
    <LegalConsentGate>
      <GroupProvider>
        <PendingInviteConsumer />
        <PlanLimitBanner />
        <TourProvider>
          {isDesktop ? (
            <div className="app-shell">
              {skipLink}
              <Sidebar />
              {main}
            </div>
          ) : (
            <>
              {skipLink}
              <AppBar />
              {main}
              <Navbar />
            </>
          )}
        </TourProvider>
      </GroupProvider>
    </LegalConsentGate>
  );
}

function App() {
  const { user } = useAuth();
  // The marketing page belongs to visitors who are not signed in on the web.
  // Inside the installed app it would replace the dashboard the user opened.
  const showPublicHome = !user && !Capacitor.isNativePlatform();

  return (
    <>
      {/* Outside Routes so it shows on the login screen too — signed-out
          visitors on the old host are exactly who needs to know. */}
      <LegacyDomainNotice />
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route element={<PublicLayout />}>
          {/* Hebrew keeps the bare paths; the other languages sit under a
              prefix so each one is its own indexable URL. */}
          {PUBLIC_LANGS.map((lang) => [
            /* The marketing page is the home page - that is the entry point
               people should find first. Signed-in visitors and the native app
               fall through to ProtectedShell instead, because the PWA start_url
               points here and they expect their dashboard. */
            (showPublicHome || lang !== DEFAULT_PUBLIC_LANG)
              ? <Route key={`${lang}-home`} path={publicPath(lang, '/')} element={<About />} />
              : null,
            <Route key={`${lang}-about`} path={publicPath(lang, '/about')} element={<About />} />,
            <Route key={`${lang}-install`} path={publicPath(lang, '/install')} element={<Install />} />,
            <Route key={`${lang}-pricing`} path={publicPath(lang, '/pricing')} element={<Pricing />} />,
            <Route key={`${lang}-faq`} path={publicPath(lang, '/faq')} element={<Faq />} />,
            <Route key={`${lang}-privacy`} path={publicPath(lang, '/privacy')} element={<LegalPage doc="privacy" />} />,
            <Route key={`${lang}-terms`} path={publicPath(lang, '/terms')} element={<LegalPage doc="terms" />} />,
            <Route key={`${lang}-refunds`} path={publicPath(lang, '/refunds')} element={<LegalPage doc="refunds" />} />,
            <Route key={`${lang}-how-to-cancel`} path={publicPath(lang, '/how-to-cancel')} element={<LegalPage doc="howToCancel" />} />,
            /* Routed so a lawyer can preview. Footer, sitemap and prerender
               stay behind VITE_REFERRAL_TERMS_PUBLISHED (off by default). */
            <Route key={`${lang}-referral-terms`} path={publicPath(lang, '/referral-terms')} element={<LegalPage doc="referralTerms" />} />,
            <Route key={`${lang}-accessibility`} path={publicPath(lang, '/accessibility')} element={<LegalPage doc="accessibility" />} />,
            <Route key={`${lang}-delete-account`} path={publicPath(lang, '/delete-account')} element={<DeleteAccount />} />,
          ])}
        </Route>
        <Route path="/guest/*" element={<GuestShell />} />
        <Route path="/welcome" element={<Welcome />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/logout" element={<Logout />} />
        <Route path="/join/:code" element={<JoinByCode />} />
        <Route path="/i/:code" element={<ReferralLanding />} />
        {/* Top-level, outside ProtectedShell/GroupProvider — admin isn't scoped
            to an active group and must be declared before the "/*" catch-all
            below or it would never match. */}
        <Route path="/admin/*" element={<AdminGuard><AdminLayout /></AdminGuard>} />
        <Route path="/*" element={<ProtectedShell />} />
      </Routes>
      </Suspense>
      <Toaster
        position="bottom-center"
        containerStyle={{ bottom: 'calc(var(--bottom-nav-height) + var(--safe-bottom) + 12px)' }}
        toastOptions={{
          duration: 3000,
          style: {
            fontFamily: 'Rubik, sans-serif',
            fontSize: 'var(--text-small)',
            background: 'var(--surface-raised)',
            color: 'var(--color-fg)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-3)',
            padding: '12px 16px',
            maxWidth: 'min(92vw, 420px)',
          },
          success: { iconTheme: { primary: 'var(--color-primary-fill)', secondary: 'var(--color-on-primary)' } },
          error: { iconTheme: { primary: 'var(--color-danger)', secondary: '#fff' }, duration: 4500 },
        }}
      />
    </>
  );
}

export default App;
